import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { EventsSearchInputComponent } from "@shared/components/events-search-input/events-search-input.component";
import { HasRoleDirective } from "@shared/directives/has-role/has-role.directive";
import { MenuItem, MessageService } from "primeng/api";
import { BadgeModule } from "primeng/badge";
import { PopoverModule } from "primeng/popover";
import { ToastModule } from "primeng/toast";
import { map, Subscription, withLatestFrom } from "rxjs";
import { AuthService } from "../../../app/services/auth.service";
import { CartService } from "../../../app/services/cart.service";
import { LocalStorageService } from "../../../app/services/local-storage.service";
import { SnackBarService } from "../../../app/services/snack-bar.service";
import { ButtonComponent } from "../button/button.component";
import { ModalLoginComponent } from "../modal-login/modal-login.component";
import { ModalSignupComponent } from "../modal-signup/modal-signup.component";
import { SidebarComponent } from "../sidebar/sidebar.component";
import { CityDropdownComponent } from "./city-dropdown/city-dropdown.component";
import { HamburguerMenuComponent } from "./hamburguer-menu/hamburguer-menu.component";
import { NavbarNotificationsComponent } from "./navbar-notifications/navbar-notifications.component";

@Component({
  selector: "app-navbar",
  providers: [MessageService],
  imports: [
    CommonModule,
    RouterModule,
    MatSelectModule,
    MatIconModule,
    MatSlideToggleModule,
    FormsModule,
    SidebarComponent,
    TranslateModule,
    ToastModule,
    ButtonComponent,
    BadgeModule,
    PopoverModule,
    HamburguerMenuComponent,
    CityDropdownComponent,
    NavbarNotificationsComponent,
    HasRoleDirective,
    EventsSearchInputComponent,
  ],
  templateUrl: "./navbar.component.html",
  styleUrl: "./navbar.component.scss",
})
export class NavbarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() isProducerArea: boolean = false;
  @Input() isLoggedIn = false;

  private token: string | null = null;
  name: string | null = null;
  lastName: string | null = null;
  private authSubscription!: Subscription;
  private dialogRef:
    | MatDialogRef<ModalLoginComponent>
    | MatDialogRef<ModalSignupComponent>
    | null = null;
  userLanguage: string = "pt";
  items: MenuItem[];
  isSeller = false;

  isNavbarOpen = false;

  currentUser = JSON.parse(
    this.localStorageService.getItem("current_user") ?? "null",
  );

  redirectUrl$ = this.activatedRoute.queryParamMap.pipe(
    map((params) => params.get("redirectUrl")),
  );

  protected readonly cartItemCount;

  constructor(
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackBarService,
    private readonly messageService: MessageService,
    private readonly localStorageService: LocalStorageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly cartService: CartService,
  ) {
    this.cartItemCount = this.cartService.cartItemCount;
    // Initialize user data immediately to avoid change detection issues
    this.token = this.localStorageService.getItem("auth-token");
    this.name = this.currentUser?.name
      ? this.currentUser.name
      : this.localStorageService.getItem("userName");
    this.lastName = this.currentUser?.last_name
      ? this.currentUser.last_name
      : this.localStorageService.getItem("userLastName");
    this.items = [
      {
        label: "SAIR",
        command: () => {
          this.delete();
        },
      },
    ];
  }

  toggleNavbar() {
    this.isNavbarOpen = !this.isNavbarOpen;
    if (this.isNavbarOpen) {
      this.openHamburguerMenu();
    } else {
      this.closeHamburguerMenu();
    }
  }

  goToMyOrders() {
    this.router.navigate(["/my-orders"]);
  }

  goToCart() {
    this.router.navigate(["/cart"]);
  }

  goToSellerEvents() {
    this.router.navigate(["/seller-events"]);
  }

  goToProfile() {
    this.router.navigate(["/my-page"]);
  }
  goToCheckIn() {
    this.router.navigate(["/check-in"]);
  }

  ngOnInit() {
    this.setUserLanguage();
    this.authSubscription = this.authService.isLoggedIn.subscribe(
      (isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        this.refreshUserState();
        if (isLoggedIn) {
          this.cartService.refreshCartCount();
        } else {
          this.cartService.resetCartCount();
        }
      },
    );
    const savedLanguage = this.localStorageService.getItem("language");
    if (savedLanguage) {
      this.translate.use(savedLanguage);
    }

    this.refreshUserState();
  }

  ngAfterViewInit() {
    this.subscribeToRedirectUrl();
  }

  save(severity: string) {
    this.messageService.add({
      severity: severity,
      summary: "Success",
      detail: "Data Saved",
    });
  }
  update() {
    this.messageService.add({
      severity: "success",
      summary: "Success",
      detail: "Data Updated",
    });
  }

  delete() {
    this.messageService.add({
      severity: "success",
      summary: "Success",
      detail: "Data Deleted",
    });
  }
  setUserLanguage(): void {
    if (typeof navigator !== "undefined") {
      const browserLang = navigator.language || navigator.languages[0];
      if (browserLang.startsWith("pt")) {
        this.userLanguage = "pt";
      } else if (browserLang.startsWith("es")) {
        this.userLanguage = "es";
      } else {
        this.userLanguage = "en";
      }
    } else {
      this.userLanguage = "pt";
    }
  }

  changeLanguage(language: string) {
    this.userLanguage = language;
    this.translate.use(language);
    this.localStorageService.setItem("language", language);
  }
  openModalLogin() {
    this.dialogRef = this.dialog.open(ModalLoginComponent, {
      width: "500px",
      maxWidth: "95vw",
      maxHeight: "90dvh",
    });

    this.dialogRef
      .afterClosed()
      .pipe(withLatestFrom(this.redirectUrl$))
      .subscribe(([userLogged, redirectUrl]) => {
        if (userLogged) {
          this.isLoggedIn = true;
          this.checkLoginStatus();

          if (redirectUrl) {
            this.router.navigateByUrl(redirectUrl);
          }
        }
      });
  }

  openModalSignUp() {
    this.dialogRef = this.dialog.open(ModalSignupComponent, {
      width: "800px",
      maxWidth: "95vw",
    });
  }

  openCreateEventsPage() {
    this.router.navigate(["/create-event"]);
  }

  logout() {
    this.authService.logout().subscribe({
      next: (response) => {
        if (response.status === 200) {
          this.snackbarService.openSnackBar(
            "Logout Efetuado com sucesso!",
            "Fechar",
            "success",
          );
          this.router.navigate(["/"]);

          this.localStorageService.clear();
          this.isLoggedIn = false;
          this.refreshUserState();
        }
      },
      error: (error) => {
        console.error("Erro durante o logout", error);
      },
    });
  }

  private checkLoginStatus() {
    this.isLoggedIn = !!this.localStorageService.getItem("auth-token");
    this.refreshUserState();
  }

  private refreshUserState(): void {
    this.currentUser = JSON.parse(
      this.localStorageService.getItem("current_user") ?? "null",
    );
    this.name =
      this.currentUser?.name ?? this.localStorageService.getItem("userName");
    this.lastName =
      this.currentUser?.last_name ??
      this.localStorageService.getItem("userLastName");
    this.isSeller = Boolean(this.currentUser?.seller_assignments?.length > 0);
  }

  private subscribeToRedirectUrl() {
    this.redirectUrl$.subscribe((redirectUrl) => {
      if (!redirectUrl) {
        return;
      }
      if (this.isLoggedIn) {
        this.router.navigateByUrl(redirectUrl);
      } else {
        this.openModalLogin();
      }
    });
  }

  ngOnDestroy() {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  goToHome() {
    this.router.navigate(["/"]);
  }

  openHamburguerMenu() {
    scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
  }

  closeHamburguerMenu() {
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, scrollY);
  }
}
